// ANALYSIS AND CHANGES:
// 1. UNIFICATION: this page now combines what `CareersPage` and `JoinPage` did.
//    - The job listing stays the same.
//    - Clicking a position opens a modal with the details and the application form instead of navigating to another page.
// 2. ABSTRACTION VIA HOOKS:
//    - `useJobPositionsManager`: a new custom hook that manages the state and handlers of the position CRUD modals (Add, Edit, Delete).
//    - `useApplicationForm`: a new custom hook that manages the state and logic of the application form (resume upload, submit, thank-you modal).
// 3. COMPONENTIZATION:
//    - The position card was extracted into the `JobPositionCard` component.
//    - The application form now lives in the `JobApplicationModal` modal.
//    - The "Why Work With Us" section was extracted into the `WhyWorkWithUsSection` component.
// 4. CLEANUP: every `console.log` was removed. The logic is cleaner and focused on rendering.

import { PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Skeleton, Typography } from "antd";
import { useState } from "react";
import { useAuth, useJobPositions } from "@hooks";
import {
  AddJobPositionModal,
  JobApplicationModal,
  JobPositionCard,
  WhyWorkWithUsSection,
} from "@components/organisms"; // Assuming the components were moved to organisms
import { useJobPositionsManager } from "@hooks/useJobPositionsManager"; // Hook customizado
import { useApplicationForm } from "@hooks/useApplicationForm"; // Hook customizado
import "../careersStyles.css";

const { Title, Paragraph } = Typography;

type FilterType = "All" | "Operations" | "Management" | "Snow Removal" | "Maintenance" | "Administration";

export const CareersPage = () => {
  const { profile } = useAuth();
  const { jobPositions } = useJobPositions();
  const [activeFilter, setActiveFilter] = useState<FilterType>("All");

  const {
    isAddEditModalOpen,
    editingPosition,
    positionToDelete,
    handleOpenAddModal,
    handleOpenEditModal,
    handleCloseAndResetModal,
    handleSetPositionToDelete,
    handleConfirmDelete,
    handleSavePosition,
  } = useJobPositionsManager();

  const {
    isApplicationModalOpen,
    isThankYouVisible,
    selectedPositionForApplication,
    handleOpenApplicationModal,
    handleCloseApplicationModal,
    handleCloseThankYouModal,
    applicationFormProps,
  } = useApplicationForm();

  const isAuthenticated = !!profile.data;

  const filteredPositions =
    activeFilter === "All"
      ? jobPositions.data || []
      : (jobPositions.data || []).filter((position) => position.department === activeFilter);

  return (
    <div className="bg-charcoal">
      {/* Hero Section */}
      <section className="relative w-full h-[400px]">
        <div className="estabrook-bg opacity-40 absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 z-10">
          <Title level={1} className="text-center">
            JOIN OUR TEAM
          </Title>
          <Paragraph className="text-secondary text-center md:text-lg">
            Build your career with a company that values skill, safety, and innovation
          </Paragraph>
        </div>
      </section>

      {/* Openings Section */}
      <section className="w-full py-12 bg-charcoal">
        <div className="container mx-auto px-4">
          <Title level={2} className="text-center mt-4 mb-4">
            CURRENT OPENINGS
          </Title>
          <Paragraph className="text-secondary text-center mb-12">
            Explore opportunities to join our growing team and build a rewarding career
          </Paragraph>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div className="flex flex-wrap justify-start gap-2 mb-4 md:mb-0">
              {(["All", "Operations", "Management", "Snow Removal", "Maintenance", "Administration"] as FilterType[]).map((filter) => (
                <Button
                  key={filter}
                  type="default"
                  className={`filter-button ${activeFilter === filter ? "filter-active" : ""}`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </Button>
              ))}
            </div>

            {isAuthenticated && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAddModal} className="btn-double-cut">
                ADD POSITION
              </Button>
            )}
          </div>

          {jobPositions.isLoading && (
            <div className="position-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-rock-grey overflow-hidden p-6">
                  <Skeleton active paragraph={{ rows: 4 }} />
                </div>
              ))}
            </div>
          )}

          {jobPositions.isError && (
            <div className="text-center py-8">
              <Title level={4} className="!text-white">ERROR LOADING POSITIONS</Title>
              <Button onClick={() => jobPositions.refetch()} className="mt-4" type="primary">
                TRY AGAIN
              </Button>
            </div>
          )}

          {!jobPositions.isLoading && !jobPositions.isError && (
            <div className="position-grid">
              {filteredPositions.length > 0 ? (
                filteredPositions.map((position) => (
                  <JobPositionCard
                    key={position.id}
                    position={position}
                    isAuthenticated={isAuthenticated}
                    onViewDetails={() => handleOpenApplicationModal(position)}
                    onEdit={() => handleOpenEditModal(position)}
                    onDelete={() => handleSetPositionToDelete(position.id)}
                  />
                ))
              ) : (
                <div className="col-span-full text-center py-8">
                  <Title level={4} className="!text-white">NO POSITIONS FOUND</Title>
                  <Paragraph className="text-secondary">
                    Please try another category or check back later.
                  </Paragraph>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <WhyWorkWithUsSection />

      {/* Modals */}
      <AddJobPositionModal
        isOpen={isAddEditModalOpen}
        onCancel={handleCloseAndResetModal}
        onSave={handleSavePosition}
        initialValues={editingPosition}
      />

      <JobApplicationModal
        isOpen={isApplicationModalOpen}
        onCancel={handleCloseApplicationModal}
        position={selectedPositionForApplication}
        formProps={applicationFormProps}
        isThankYouVisible={isThankYouVisible}
        onCloseThankYou={handleCloseThankYouModal}
      />

      <Modal
        title="CONFIRM DELETE"
        open={!!positionToDelete}
        onOk={handleConfirmDelete}
        onCancel={() => handleSetPositionToDelete(null)}
        okText="DELETE"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to remove this position? This action is not permanent but will hide it from applicants.</p>
      </Modal>
    </div>
  );
};
